import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { healthcareApi, RequestError, type InterviewSchedule } from '../api/client';
import { toConsentBlock } from '../utils/healthcareConsent';
import { formatRecordedAt, isCancelledSchedule } from '../utils/interviewRecords';

interface Props {
  personId: string;
  /** 出す件数（個人詳細では直近の数件だけ）。多いときは面談設定の画面へ案内する */
  limit?: number;
}

/**
 * 登録済みの面談予定（日時・場所・URL・メモ）の一覧（課題 #71 / BE PR #144）。
 *
 * 面談予定の「メモ」は登録できても見る画面が無かった（2026-09-30 高尾さん「見たいのに見られない」）。
 * 予定を登録すると個人詳細に戻るため、個人詳細と面談設定の両方に出す。
 * 予定の一覧の API が無い古い BE（404）や、同意が無く BE が止めたときは何も出さない
 * （同意が無いことは、個人詳細・面談設定の画面が別に表示する）。
 */
export default function InterviewScheduleList({ personId, limit }: Props) {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const [schedules, setSchedules] = useState<InterviewSchedule[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    let cancelled = false;
    // 1 件多く取り、ほかにもあるかを見分ける
    healthcareApi
      .listInterviews(personId, token, limit ? limit + 1 : undefined)
      .then((res) => {
        if (!cancelled) setSchedules(Array.isArray(res?.data) ? res.data : null);
      })
      .catch((err) => {
        if (cancelled) return;
        // 古い BE（ルートが無い 404）と同意起因は出さない。それ以外は読めなかったと出す
        const routeMissing = err instanceof RequestError && err.status === 404 && !err.errorCode;
        if (!routeMissing && !toConsentBlock(err)) setError(true);
        setSchedules(null);
      });
    return () => {
      cancelled = true;
    };
  }, [getToken, personId, limit]);

  if (!schedules && !error) return null;

  const shown = schedules ? (limit ? schedules.slice(0, limit) : schedules) : [];
  const more = Boolean(limit && schedules && schedules.length > limit);

  return (
    <div className="healthcare-card interview-schedules" data-testid="interview-schedules">
      <h3>{t('healthcare.schedules.title')}</h3>
      {error ? (
        <p className="healthcare-error-text">{t('healthcare.schedules.error')}</p>
      ) : shown.length === 0 ? (
        <p>{t('healthcare.schedules.empty')}</p>
      ) : (
        <ul className="interview-schedule-list">
          {shown.map((s) => (
            <li key={s.id} className="interview-schedule-item" data-testid="interview-schedule">
              <p className="interview-schedule-when">
                <strong>{formatRecordedAt(s.scheduled_at)}</strong>
                {isCancelledSchedule(s) && <span className="interview-schedule-tag">{t('healthcare.schedules.cancelled')}</span>}
                {s.record_id && <span className="interview-schedule-tag">{t('healthcare.schedules.recorded')}</span>}
              </p>
              {s.location && (
                <p className="interview-schedule-line">
                  {t('healthcare.schedules.location')}: {s.location}
                </p>
              )}
              {s.meeting_url && (
                <p className="interview-schedule-line">
                  {t('healthcare.schedules.meetingUrl')}:{' '}
                  <a href={s.meeting_url} target="_blank" rel="noopener noreferrer">
                    {s.meeting_url}
                  </a>
                </p>
              )}
              <p className="interview-schedule-notes" data-testid="interview-schedule-notes">
                {s.notes ? s.notes : <span className="interview-schedule-empty">{t('healthcare.schedules.noNotes')}</span>}
              </p>
            </li>
          ))}
        </ul>
      )}
      {more && (
        <p className="healthcare-records-link">
          <Link to={`/healthcare/person/${personId}/interview`}>{t('healthcare.schedules.seeAll')}</Link>
        </p>
      )}
    </div>
  );
}
